import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Upload, Plus, Trash2, Calendar, Clock, Users, 
  Globe, BookOpen, Target, Award, X, Check, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CourseBuilder({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const { id: courseId } = useParams(); // Get course ID from URL for edit mode
  const isEditMode = !!courseId;
  
  // Basic Info
  const [sportsCategories, setSportsCategories] = useState([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [skillCategories, setSkillCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [languages, setLanguages] = useState([]);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    sport_category: '',
    skill_category: '',
    custom_skill: '',
    title: '',
    description: '',
    level: 'beginner',
    age_group: 'All ages',
    price: '',
    currency: 'INR',
    course_type: 'multi_session',
    num_sessions: 8,
    session_date: '',
    session_time: '',
    max_students: 20,
    includes_live_sessions: true,
    includes_recorded_videos: false,
    language: 'English',
    additional_languages: [],
  });
  
  const [modules, setModules] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchInitialData();
    if (isEditMode) {
      fetchCourseData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (formData.sport_category) {
      fetchSkillCategories(formData.sport_category);
    }
  }, [formData.sport_category]);
  
  const fetchInitialData = async () => {
    try {
      const [sportsRes, ageGroupsRes, languagesRes] = await Promise.all([
        axios.get(`${API}/sports/categories`, { withCredentials: true }).catch(err => {
          console.error('Sports fetch error:', err);
          return { data: [] };
        }),
        axios.get(`${API}/age-groups`, { withCredentials: true }).catch(err => {
          console.error('Age groups fetch error:', err);
          return { data: { age_groups: ['All ages'] } };
        }),
        axios.get(`${API}/languages`, { withCredentials: true }).catch(err => {
          console.error('Languages fetch error:', err);
          return { data: { languages: ['English'] } };
        })
      ]);
      
      setSportsCategories(Array.isArray(sportsRes.data) ? sportsRes.data : []);
      setAgeGroups(ageGroupsRes.data.age_groups || ['All ages']);
      setLanguages(languagesRes.data.languages || ['English']);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      // Set defaults so component doesn't crash
      setSportsCategories([]);
      setAgeGroups(['All ages']);
      setLanguages(['English']);
      toast.error('Failed to load some form options. You can still create a course.');
    }
  };
  
  const fetchCourseData = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      const course = response.data;
      
      // Populate form with existing course data
      setFormData({
        sport_category: course.sport_category || '',
        skill_category: course.skill_category || '',
        custom_skill: course.custom_skill || '',
        title: course.title || '',
        description: course.description || '',
        level: course.level || 'beginner',
        age_group: course.age_group || 'All ages',
        price: course.price || '',
        currency: course.currency || 'INR',
        course_type: course.course_type || 'multi_session',
        num_sessions: course.num_sessions || 8,
        session_date: course.session_date || '',
        session_time: course.session_time || '',
        max_students: course.max_students || 20,
        includes_live_sessions: course.includes_live_sessions ?? true,
        includes_recorded_videos: course.includes_recorded_videos ?? false,
        language: course.language || 'English',
        additional_languages: course.additional_languages || [],
      });
      
      if (course.modules) {
        setModules(course.modules);
      }
      
      if (course.cloudinary_image_url) {
        setImagePreview(course.cloudinary_image_url);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course data');
      navigate('/coach/dashboard');
    }
  };
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
      toast.success('Course deleted successfully');
      setShowDeleteModal(false);
      navigate('/coach/dashboard');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    } finally {
      setDeleting(false);
    }
  };
  
  const fetchSkillCategories = async (sport) => {
    try {
      const response = await axios.get(`${API}/skills/${encodeURIComponent(sport)}`, {
        withCredentials: true
      });
      setSkillCategories(response.data.skills || []);
    } catch (error) {
      console.error('Error fetching skills:', error);
      setSkillCategories(['Other (Custom)']);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };
  
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      return {
        url: response.data.url,
        public_id: response.data.public_id
      };
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };
  
  const addModule = () => {
    const newModule = {
      module_id: `mod_${Date.now()}`,
      title: '',
      description: '',
      skill_focus: '',
      duration_minutes: 60,
      order: modules.length + 1
    };
    setModules([...modules, newModule]);
  };
  
  const updateModule = (index, field, value) => {
    const updated = [...modules];
    updated[index][field] = value;
    setModules(updated);
  };
  
  const removeModule = (index) => {
    setModules(modules.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.sport_category || !formData.skill_category || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.course_type === 'single_session' && (!formData.session_date || !formData.session_time)) {
      toast.error('Please set session date and time for live sessions');
      return;
    }
    
    if (formData.course_type === 'multi_session' && modules.length === 0) {
      toast.error('Please add at least one module for multi-session courses');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Upload image if selected
      let imageData = null;
      if (imageFile) {
        imageData = await uploadImage();
        if (!imageData) {
          setSubmitting(false);
          return;
        }
      }
      
      // Prepare payload
      const payload = {
        ...formData,
        cloudinary_image_url: imageData?.url || imagePreview || null,
        cloudinary_public_id: imageData?.public_id || null,
        modules: modules
      };
      
      // Create or update course
      let response;
      if (isEditMode) {
        response = await axios.put(`${API}/courses/${courseId}`, payload, { withCredentials: true });
        toast.success('Course updated successfully!');
      } else {
        response = await axios.post(`${API}/courses`, payload, { withCredentials: true });
        toast.success('Course created successfully!');
      }
      
      if (onSuccess) {
        onSuccess(response.data);
      } else {
        navigate('/coach/dashboard');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} course`);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-imperial py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-dark-purple rounded-xl p-8 border border-purple-800/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-white">Create New Course</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sport & Skill Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Sport <span className="text-energy-red">*</span>
                </label>
                <select
                  value={formData.sport_category}
                  onChange={(e) => {
                    handleInputChange('sport_category', e.target.value);
                    handleInputChange('skill_category', '');
                  }}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                  required
                >
                  <option value="">Select Sport</option>
                  {Array.isArray(sportsCategories) && sportsCategories.length > 0 ? (
                    sportsCategories.map(cat => {
                      const categoryName = cat.category_name || cat.name || 'Unknown';
                      const sportsList = Array.isArray(cat.sports) ? cat.sports : [];
                      
                      return (
                        <optgroup key={cat.category_id || cat.id || categoryName} label={categoryName}>
                          {sportsList.map(sport => {
                            // Handle both string and object formats
                            const sportName = typeof sport === 'string' ? sport : (sport.name || sport.title || String(sport));
                            const sportValue = typeof sport === 'string' ? sport : (sport.name || sport.title || String(sport));
                            return (
                              <option key={sportValue} value={sportValue}>
                                {sportName}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    })
                  ) : (
                    <option disabled>Loading sports...</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Skill/Topic <span className="text-energy-red">*</span>
                </label>
                <select
                  value={formData.skill_category}
                  onChange={(e) => handleInputChange('skill_category', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                  required
                  disabled={!formData.sport_category}
                >
                  <option value="">Select Skill</option>
                  {Array.isArray(skillCategories) && skillCategories.map(skill => {
                    // Handle both string and object formats
                    const skillName = typeof skill === 'string' ? skill : (skill.name || skill.title || String(skill));
                    const skillValue = typeof skill === 'string' ? skill : (skill.name || skill.title || String(skill));
                    return (
                      <option key={skillValue} value={skillValue}>
                        {skillName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            {/* Custom Skill if Other selected */}
            {formData.skill_category === 'Other (Custom)' && (
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Custom Skill Name <span className="text-energy-red">*</span>
                </label>
                <input
                  type="text"
                  value={formData.custom_skill}
                  onChange={(e) => handleInputChange('custom_skill', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                  placeholder="e.g., Advanced Tactics"
                  required
                />
              </div>
            )}
            
            {/* Course Title */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Course Title <span className="text-energy-red">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                placeholder="e.g., Master Football Dribbling"
                required
              />
              <p className="text-xs text-white/40 mt-1">
                Display: "{formData.title} {formData.age_group !== 'All ages' ? `(${formData.age_group})` : ''}"
              </p>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Description <span className="text-energy-red">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none h-32"
                placeholder="Describe what students will learn..."
                required
              />
            </div>
            
            {/* Level & Age Group */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Skill Level <span className="text-energy-red">*</span>
                </label>
                <select
                  value={formData.level}
                  onChange={(e) => handleInputChange('level', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                >
                  <option value="beginner">Beginner</option>
                  <option value="advanced">Advanced</option>
                  <option value="professional">Professional</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Age Group <span className="text-energy-red">*</span>
                </label>
                <select
                  value={formData.age_group}
                  onChange={(e) => handleInputChange('age_group', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                >
                  {Array.isArray(ageGroups) && ageGroups.map(age => {
                    // Handle both string and object formats
                    const ageName = typeof age === 'string' ? age : (age.name || age.label || String(age));
                    const ageValue = typeof age === 'string' ? age : (age.value || age.name || String(age));
                    return (
                      <option key={ageValue} value={ageValue}>
                        {ageName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            {/* Course Type */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-3">
                Course Type <span className="text-energy-red">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleInputChange('course_type', 'single_session')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.course_type === 'single_session'
                      ? 'border-premium-gold bg-premium-gold/10'
                      : 'border-purple-800/50 hover:border-premium-gold/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-premium-gold" />
                    <div className="text-left">
                      <div className="font-semibold text-white">Single Session</div>
                      <div className="text-xs text-white/60">1-hour live session</div>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleInputChange('course_type', 'multi_session')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.course_type === 'multi_session'
                      ? 'border-premium-gold bg-premium-gold/10'
                      : 'border-purple-800/50 hover:border-premium-gold/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-premium-gold" />
                    <div className="text-left">
                      <div className="font-semibold text-white">Multi-Session</div>
                      <div className="text-xs text-white/60">Multiple structured sessions</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Single Session Details */}
            {formData.course_type === 'single_session' && (
              <div className="bg-imperial/50 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-premium-gold" />
                  Live Session Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">
                      Date <span className="text-energy-red">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.session_date}
                      onChange={(e) => handleInputChange('session_date', e.target.value)}
                      className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">
                      Time <span className="text-energy-red">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.session_time}
                      onChange={(e) => handleInputChange('session_time', e.target.value)}
                      className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">
                      Max Students <span className="text-energy-red">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.max_students}
                      onChange={(e) => handleInputChange('max_students', e.target.value)}
                      className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Multi-Session Details */}
            {formData.course_type === 'multi_session' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">
                      Number of Sessions <span className="text-energy-red">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.num_sessions}
                      onChange={(e) => handleInputChange('num_sessions', e.target.value)}
                      className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                      min="2"
                      max="52"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">
                      Max Students <span className="text-energy-red">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.max_students}
                      onChange={(e) => handleInputChange('max_students', e.target.value)}
                      className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                </div>
                
                {/* Module Builder */}
                <div className="bg-imperial/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-premium-gold" />
                      Course Modules/Sessions
                    </h3>
                    <button
                      type="button"
                      onClick={addModule}
                      className="flex items-center gap-2 text-premium-gold hover:text-premium-gold/80 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Module
                    </button>
                  </div>
                  
                  {modules.length === 0 ? (
                    <p className="text-white/40 text-center py-4">
                      No modules added yet. Click "Add Module" to start.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {modules.map((module, index) => (
                        <div key={module.module_id} className="bg-dark-purple rounded-lg p-4 border border-purple-800/30">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-premium-gold">
                              Module {index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeModule(index)}
                              className="text-energy-red hover:text-energy-red/80"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={module.title}
                              onChange={(e) => updateModule(index, 'title', e.target.value)}
                              className="w-full bg-imperial border border-purple-800/50 rounded px-3 py-2 text-white text-sm focus:border-premium-gold focus:outline-none"
                              placeholder="Module title"
                              required
                            />
                            <textarea
                              value={module.description}
                              onChange={(e) => updateModule(index, 'description', e.target.value)}
                              className="w-full bg-imperial border border-purple-800/50 rounded px-3 py-2 text-white text-sm focus:border-premium-gold focus:outline-none h-20"
                              placeholder="Module description"
                              required
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={module.skill_focus}
                                onChange={(e) => updateModule(index, 'skill_focus', e.target.value)}
                                className="w-full bg-imperial border border-purple-800/50 rounded px-3 py-2 text-white text-sm focus:border-premium-gold focus:outline-none"
                                placeholder="Skill focus"
                              />
                              <input
                                type="number"
                                value={module.duration_minutes}
                                onChange={(e) => updateModule(index, 'duration_minutes', parseInt(e.target.value))}
                                className="w-full bg-imperial border border-purple-800/50 rounded px-3 py-2 text-white text-sm focus:border-premium-gold focus:outline-none"
                                min="30"
                                max="180"
                                placeholder="Duration (min)"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Price & Language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Price (INR) <span className="text-energy-red">*</span>
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                  min="0"
                  step="0.01"
                  placeholder="2999"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Language <span className="text-energy-red">*</span>
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  className="w-full bg-imperial border border-purple-800/50 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:outline-none"
                >
                  {Array.isArray(languages) && languages.map(lang => {
                    // Handle both string and object formats
                    const langName = typeof lang === 'string' ? lang : (lang.name || lang.label || String(lang));
                    const langValue = typeof lang === 'string' ? lang : (lang.value || lang.name || String(lang));
                    return (
                      <option key={langValue} value={langValue}>
                        {langName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Course Image
              </label>
              <div className="flex items-start gap-4">
                {imagePreview ? (
                  <div className="relative w-48 h-27 rounded-lg overflow-hidden">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                      }}
                      className="absolute top-2 right-2 bg-energy-red text-white p-1 rounded-full hover:bg-energy-red-dark"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="w-48 h-27 border-2 border-dashed border-purple-800/50 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-premium-gold/50 transition-colors">
                    <Upload className="w-8 h-8 text-white/40 mb-2" />
                    <span className="text-xs text-white/60">Upload Image</span>
                    <span className="text-xs text-white/40">(Max 5MB)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
            
            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6 border-t border-premium-gold/20">
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="bg-dark-surface border border-red-500 text-red-500 hover:bg-red-500/10 font-semibold py-3 px-6 rounded-lg transition-all flex items-center gap-2"
                >
                  <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                  Delete Course
                </button>
              )}
              
              <div className="flex-1 flex gap-4">
                {onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-transparent border border-premium-gold/30 text-white hover:bg-dark-surface/50 font-semibold py-3 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="flex-1 bg-premium-gold hover:opacity-90 text-deep-dark font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting || uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-deep-dark/20 border-t-deep-dark"></div>
                      {uploading ? 'Uploading...' : isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" strokeWidth={1.5} />
                      {isEditMode ? 'Update Course' : 'Create Course'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-surface rounded-xl max-w-md w-full p-6 border border-premium-gold/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                Delete Course
              </h3>
            </div>
            
            <p className="text-soft-lavender mb-6">
              Are you sure you want to delete this course? This action cannot be undone. All enrolled students will lose access to this course.
            </p>
            
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 bg-transparent border border-premium-gold text-premium-gold hover:bg-premium-gold/10 font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                    Delete Course
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseBuilder;
